import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Theme,
  useTheme
} from "@mui/material";
import React, { useMemo } from "react";
import { ModernSelectProps } from "./ModernSelect.types";

export const getDefaultSelectSx = (
  colorTheme: ModernSelectProps["colorTheme"],
  size: ModernSelectProps["size"],
  theme: Theme
) => {
  if (colorTheme === "dark") {
    return {
      borderRadius: 2,
      backgroundColor: "transparent",
      fontSize: "0.875rem",
      height: size === "small" ? 32 : 40,
      color: "white",
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "rgba(255, 255, 255, 0.3)"
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "rgba(255, 255, 255, 0.7)"
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "white",
        borderWidth: 1
      },
      "& .MuiSelect-select": {
        display: "flex",
        alignItems: "center",
        gap: 1,
        fontWeight: 500,
        color: "white"
      },
      "& .MuiSelect-icon": {
        color: "white"
      }
    };
  }

  // Light theme by default
  return {
    borderRadius: 2,
    backgroundColor: theme.palette.background.paper,
    fontSize: "0.875rem",
    height: size === "small" ? 32 : 40,
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
      borderWidth: 2
    },
    "& .MuiSelect-select": {
      display: "flex",
      alignItems: "center",
      gap: 1,
      fontWeight: 500
    }
  };
};

export const getDefaultFormControlSx = (
  colorTheme: ModernSelectProps["colorTheme"],
  minWidth: ModernSelectProps["minWidth"],
  autoWidth: ModernSelectProps["autoWidth"],
  theme: Theme
) => {
  if (colorTheme === "dark") {
    return {
      minWidth: minWidth || (autoWidth ? "auto" : undefined),
      "& .MuiInputLabel-root": {
        fontSize: "0.875rem",
        fontWeight: 500,
        color: "rgba(255, 255, 255, 0.7)",
        "&.Mui-focused": {
          color: "white"
        }
      }
    };
  }

  // Light theme by default
  return {
    minWidth: minWidth || (autoWidth ? "auto" : undefined),
    "& .MuiInputLabel-root": {
      fontSize: "0.875rem",
      fontWeight: 500,
      "&.Mui-focused": {
        color: theme.palette.primary.main
      }
    }
  };
};

const ModernSelect: React.FC<ModernSelectProps> = ({
  options,
  value,
  onChange,
  valueKey = "value",
  labelKey = "label",
  showIndicatorDot = false,
  showIcons = false,
  colorTheme = "light",
  id,
  label,
  placeholder,
  variant = "outlined",
  size = "medium",
  disabled = false,
  fullWidth = true,
  sx = {},
  className,
  labelId,
  autoWidth = false,
  minWidth,
  ...otherProps
}) => {
  const theme = useTheme();

  // Normalize the data to work with strings and objects
  const normalizedOptions = useMemo(() => {
    return options.map((option, index) => {
      // If it is a simple string (like languages)
      if (typeof option === "string") {
        return {
          value: option,
          label: option
        };
      }

      // If it is an object (like models, scenarios)
      if (typeof option === "object" && option !== null) {
        const optionValue =
          option[valueKey] !== undefined ? option[valueKey] : option.value;
        const optionLabel =
          option[labelKey] !== undefined
            ? option[labelKey]
            : option.label || option.name;

        return {
          value: optionValue?.toString() || "",
          label: optionLabel || "",
          disabled: option.disabled || false,
          icon: option.icon,
          ...option // Preserve original data for special cases
        };
      }

      // Fallback
      return {
        value: index.toString(),
        label: String(option)
      };
    });
  }, [options, valueKey, labelKey]);

  // MenuItems
  const menuItems = useMemo(() => {
    return normalizedOptions.map((option) => (
      <MenuItem
        value={option.value}
        key={`OPTION-${option.value}`}
        disabled={option.disabled}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          fontSize: "0.875rem",
          py: 1.5,
          "&:hover": {
            backgroundColor: theme.palette.action.hover
          },
          "&.Mui-selected": {
            backgroundColor: theme.palette.primary.main + "20",
            color: theme.palette.primary.main,
            fontWeight: 600,
            "&:hover": {
              backgroundColor: theme.palette.primary.main + "30"
            }
          }
        }}
      >
        {/* Selection indicator (dot) */}
        {showIndicatorDot && (
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor:
                value?.toString() === option.value?.toString()
                  ? theme.palette.primary.main
                  : "transparent",
              border: `2px solid ${
                value?.toString() === option.value?.toString()
                  ? theme.palette.primary.main
                  : theme.palette.divider
              }`
            }}
          />
        )}

        {/* Custom icon (like stars) */}
        {showIcons && option.icon && (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {option.icon}
          </Box>
        )}

        {option.label}
      </MenuItem>
    ));
  }, [normalizedOptions, value, showIndicatorDot, showIcons, theme]);

  const defaultSelectSx = getDefaultSelectSx(colorTheme, size, theme);

  // Combine styles giving priority to the customized
  const selectSx = {
    ...defaultSelectSx,
    ...sx
  };

  const formControlSx = getDefaultFormControlSx(
    colorTheme,
    minWidth,
    autoWidth,
    theme
  );

  return (
    <FormControl
      fullWidth={fullWidth}
      variant={variant}
      sx={formControlSx}
      className={className}
    >
      {label && (
        <InputLabel
          id={labelId}
          sx={{
            transform: "translate(14px, 12px) scale(1)",
            "&.MuiInputLabel-shrink": {
              transform: "translate(14px, -9px) scale(0.75)"
            }
          }}
        >
          {label}
        </InputLabel>
      )}
      <Select
        labelId={labelId}
        id={id}
        value={value?.toString() || ""}
        label={label}
        onChange={onChange}
        disabled={disabled}
        size={size}
        autoWidth={autoWidth}
        sx={selectSx}
        MenuProps={{
          PaperProps: {
            sx: {
              borderRadius: 2,
              mt: 1,
              boxShadow: "0 4px 20px rgba(0,0,0,0.1)"
            }
          }
        }}
        {...otherProps}
      >
        {menuItems}
      </Select>
    </FormControl>
  );
};

export default ModernSelect;
